import React, { useEffect, useState } from 'react';
import { Box, List, ListItem, Paper, Popper, Typography } from '@material-ui/core';
import { RoomType } from '@lib/state';
import {
  GetRatePlanRequest,
  RoomRatePlan,
  roomRatePlanQuery,
  roomRatePlanService,
} from 'app/state';
import { useObservable } from '@lib/common';

interface Props {
  open?: boolean;
  anchorEl?: HTMLElement;
  matches: Array<RoomRatePlan>;
  roomTypes: Array<RoomType>;
  onSelect(ratePlan: RoomRatePlan): void;
}

export const RatePlanAutocompleteMenu: React.FC<Props> = ({
  open = false,
  anchorEl,
  matches,
  roomTypes,
  onSelect,
}) => {
  return (
    <Popper
      open={open && matches.length > 0}
      anchorEl={anchorEl}
      placement="bottom"
      style={{ width: anchorEl?.clientWidth }}
      keepMounted
    >
      <Paper>
        <List>
          {matches.map(r => (
            <ListItem key={r.id} button onMouseDown={() => onSelect(r)}>
              <Box>
                <Typography>{r.name}</Typography>
                {getDetails(r, roomTypes).map((s, i) => (
                  <Typography key={i} variant="body2">
                    {s}
                  </Typography>
                ))}
              </Box>
            </ListItem>
          ))}
        </List>
      </Paper>
    </Popper>
  );
};

function getDetails(
  { affiliation, roomTypeId, disabledAt }: RoomRatePlan,
  roomTypes: Array<RoomType>
) {
  return [
    affiliation?.corporateAccountName,
    roomTypes.find(r => r.id === roomTypeId)?.name,
    !disabledAt ? 'Active' : 'Inactive',
  ].filter(s => !!s);
}

export function useRatePlanAutocomplete(form: GetRatePlanRequest) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement>();
  const [open, setOpen] = useState(false);
  const loading = useObservable(roomRatePlanQuery.loading);
  const matches = useObservable(roomRatePlanQuery.matches);

  useEffect(() => {
    // on mount and unmount, reset the store so that the next rate plan lookup does not have past state
    roomRatePlanService.reset();
    return () => roomRatePlanService.reset();
  }, []);

  useEffect(() => {
    if (open) roomRatePlanService.getMatches(form);
  }, [open, form]);

  return {
    open,
    setOpen,
    anchorEl,
    setAnchorEl,
    loading,
    matches,
    getInputProps() {
      return {
        onFocus: (e: React.FocusEvent<HTMLElement>) => {
          setAnchorEl(e.target);
          setOpen(true);
        },
        onBlur: () => setOpen(false),
      };
    },
  };
}
